import { isCNLocale } from './LocaleUtils';

/**
 * Sorts an array of items alphabetically by a specified field for default locales.
 * The original array is not mutated.
 * @param items - The array of items to sort.
 * @param field - The key of the field to sort by. Defaults to 'label'.
 * @returns A new array of items sorted by the specified field.
 */
export function sortItemsForDefaultLocale<T, K extends keyof T>(
  items: Array<T>,
  field: K = 'label' as K
): Array<T> {
  return [...items].sort((a, b) => {
    const valueA = String(a[field]).toUpperCase();
    const valueB = String(b[field]).toUpperCase();
    if (valueA < valueB) {
      return -1;
    }
    if (valueA > valueB) {
      return 1;
    }
    return 0;
  });
}

/**
 * Resolves the appropriate Pinyin locale for sorting.
 * Falls back to 'zh' if no locale is specified,
 * which means it would be handled by the browser for the actual fallback.
 * @returns The resolved locale string.
 */
export function resolvePinyinLocale(): string {
  const localeOptions = ['zh-Hans-u-co-pinyin', 'zh-CN', 'zh'];
  for (const locale of localeOptions) {
    try {
      // Test if the locale is valid by using it in `Intl.Collator`
      new Intl.Collator(locale);
      return locale;
    } catch {
      // Ignore invalid locales and continue to the next one
    }
  }
  return localeOptions[localeOptions.length - 1];
}

/**
 * Sorts an array of items alphabetically by a specified field for CN locales using Pinyin sorting.
 * The original array is not mutated.
 * @param items - The array of items to sort.
 * @param field - The key of the field to sort by. Defaults to 'label'.
 * @returns A new array of items sorted by the specified field.
 */
export function sortItemsForCNLocale<T, K extends keyof T>(
  items: Array<T>,
  field: K = 'label' as K
): Array<T> {
  const resolvedPinyinLocale = resolvePinyinLocale();
  return [...items].sort((a, b) => {
    return String(a[field]).localeCompare(String(b[field]), resolvedPinyinLocale);
  });
}

/**
 * Sorts a list of items based on the provided locale and field key.
 * For CN locales, it uses Pinyin sorting.
 * For other locales, it uses default alphabetical sorting.
 * @param locale - The locale string to determine the sorting logic.
 * @param items - The array of items to sort.
 * @param field - The key of the field to sort by. Defaults to 'label'.
 * @returns A new array of sorted items.
 */
export function sortItemsByLocale<T, K extends keyof T>(
  locale: string,
  items: Array<T>,
  field: K = 'label' as K
): Array<T> {
  if (isCNLocale(locale)) {
    return sortItemsForCNLocale(items, field);
  }
  return sortItemsForDefaultLocale(items, field);
}
